import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import classnames from "classnames";
import styled from "styled-components";
import { toast } from "react-toastify";
import OrdenDeVentaForm from "../../../components/OrdenDeVentaForm";
import OrdenesDeVentaGeneradas from "../../../components/OrdenesDeVentasGeneradas";
import useFetcher from "../../../hooks/useFetcher";
import { getOrdenesDeVentaById } from "../../../services/ordenDeVentaServices";

const NavWrapper = styled.div`
  font-family: "Nunito";
  margin-bottom: 20px;
  .nav-link {
    cursor: pointer;
  }
  .active {
    background-color: #f8f8f8 !important;
    color: #293134 !important; // Grey2
    font-weight: 700;
    border-bottom: solid 1px #f8f8f8 !important;
  }
  .nav {
    margin-left: 2.5rem !important;
  }
`;

const validateCanCreateOV = (nota, ordenes, servicioOfrecido, lastOrder) => {
  // Si la nota está Anulada (6), Cancelada (7) o Pre Cancelada (9),
  // no se puede crear una orden de venta
  if ([6, 7, 9].includes(nota.estado)) {
    return false;
  }

  // Si la nota está en estado Cubierta (5),
  // se valida si fue cerrada recientemente (menos de 14 días)
  // y si no hay órdenes o la última orden tiene estado específico (4, 5 o 6)
  if (nota.estado === 5) {
    // Si no hay fecha de cierre, no se puede crear la OV
    if (!nota && !nota.fechaCerrada) return false;

    const fechaCerrada = new Date(nota.fechaCerrada);
    const hoy = new Date();
    const diferenciaDias = (hoy - fechaCerrada) / (1000 * 60 * 60 * 24);

    // Si pasaron menos de 14 días desde la fecha de cierre
    // y (no hay órdenes o la última orden tiene estado 4, 5 o 6), se habilita
    if (diferenciaDias < 14 && (!ordenes || ordenes.count === 0 || [4, 5, 6].includes(lastOrder && lastOrder.estado))) {
      return true;
    }

    // Si no cumple con las condiciones anteriores, no se habilita
    return false;
  }

  // Si no hay órdenes asociadas, se habilita la creación (primer uso)
  if (!ordenes || ordenes.count === 0) {
    return true;
  }

  // Si hay órdenes, no hay servicio ofrecido y la última tiene anticipo,
  // se habilita crear una nueva OV
  if (ordenes.count > 0 && !servicioOfrecido && lastOrder && lastOrder.anticipo === true) {
    return true;
  }

  // Si el estado de la última orden es 4, 5 o 6, se habilita también
  if ([4, 5, 6].includes(lastOrder && lastOrder.estado)) {
    return true;
  }

  // Si no cumple con ninguna condición anterior, no se habilita la creación
  return false;
};

const validateCanEditOV = (nota) => {
  // Anulada, Cancelada, Pre Cancelada
  if ([6, 7, 9].includes(nota.estado)) {
    return false;
  }

  // Cubierta
  if (nota.estado === 5) {
    if (!nota && !nota.fechaCerrada) return false;

    const fechaCerrada = new Date(nota.fechaCerrada);
    const hoy = new Date();
    const diferenciaDias = (hoy - fechaCerrada) / (1000 * 60 * 60 * 24);

    if (diferenciaDias < 14) {
      return true;
    }

    return false;
  }

  return true;
};

const OrdenesDeVentaModal = ({ toggle, isOpen, nota }) => {
  const [activeTab, setActiveTab] = useState("1");
  const [ordenDeVenta, setOrdenDeVenta] = useState(null);
  const [ordenes, isLoadingOrdenes, , setOrdenes] = useFetcher(getOrdenesDeVentaById, [nota.id]);
  const [canCreateOv, setCanCreateOv] = useState(false);
  const [canEditOV, setCanEditOV] = useState(false);

  const { servicioOfrecido } = nota;
  const lastOrder = ordenes && ordenes.rows && ordenes.rows[ordenes.rows.length - 1];

  useEffect(() => {
    if (!isLoadingOrdenes) {
      const canCreateNewOrder = validateCanCreateOV(nota, ordenes, servicioOfrecido, lastOrder);
      setCanCreateOv(canCreateNewOrder);
      setCanEditOV(validateCanEditOV(nota));
      if (!canCreateNewOrder || ordenes.count > 0) {
        setActiveTab("2");
      }
    }
  }, [lastOrder, servicioOfrecido, ordenes, isLoadingOrdenes]);

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  return (
    <div>
      <Modal isOpen={isOpen} style={{ fontFamily: "Nunito", maxWidth: "1100px" }} centered size="lg">
        <ModalHeader toggle={toggle} style={{ borderBottom: "none", background: "#f8f8f8" }}>
          <h4>
            Nota de pedido: {nota && nota.id} - {nota && nota.razonSocial}
          </h4>
          {ordenDeVenta ? <h5>Orden de venta: {ordenDeVenta && ordenDeVenta.idOrdenDeVenta}</h5> : <h5>Ordenes de venta generadas</h5>}
        </ModalHeader>
        <ModalBody style={{ background: "#f8f8f8", alignItems: "flex-start" }}>
          <NavWrapper>
            <Nav tabs>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "1" })}
                  onClick={() => {
                    if (canCreateOv) {
                      toggleTab("1");
                    } else {
                      toast.error("No se puede crear una nueva orden de venta");
                    }
                  }}
                >
                  Orden de venta
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "2" })}
                  onClick={() => {
                    toggleTab("2");
                    setOrdenDeVenta(null);
                  }}
                >
                  Ordenes de venta generadas
                </NavLink>
              </NavItem>
            </Nav>
          </NavWrapper>
          <TabContent style={{ width: "100%", margin: "0 auto" }} activeTab={activeTab}>
            <TabPane className="w-100" tabId="1">
              {activeTab === "1" && <OrdenDeVentaForm notaDePedidoId={nota.id} toggle={toggle} orden={ordenDeVenta} toggleTab={toggleTab} canEditOV={canEditOV} />}
            </TabPane>
            <TabPane className="w-100" tabId="2">
              {activeTab === "2" && (
                <OrdenesDeVentaGeneradas nota={nota} setOrdenDeVenta={setOrdenDeVenta} setOrdenes={setOrdenes} toggleTab={toggleTab} canEditOV={canEditOV} />
              )}
            </TabPane>
          </TabContent>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default OrdenesDeVentaModal;
